import React, { useCallback, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, Download, Printer } from 'lucide-react';
import useGlobalStoragePdfPreview from '../hooks/useGlobalStoragePdfPreview';
import { printPdfFromUrl } from '../utils/printPdf';
import { QUICK_START_GUIDE_STORAGE_PATH } from '../utils/supportDocumentsConfig';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const PREVIEW_WIDTH_PX = 520;
/** A4 height at preview width — keeps layout stable while pages turn */
const A4_VIEWPORT_HEIGHT_PX = Math.round(PREVIEW_WIDTH_PX * (842.25 / 595.5));

const pageProps = {
  width: PREVIEW_WIDTH_PX,
  renderAnnotationLayer: false,
  renderTextLayer: false,
  renderMode: 'canvas',
};

export default function QuickStartGuideViewer() {
  const { previewPdfUrl, loadError, loading } = useGlobalStoragePdfPreview(
    QUICK_START_GUIDE_STORAGE_PATH,
  );
  const [numPages, setNumPages] = useState(null);
  /** Page shown in the pager and kept visible during transitions */
  const [committedPage, setCommittedPage] = useState(1);
  /** Target page loading on top of committed until canvas is ready */
  const [incomingPage, setIncomingPage] = useState(null);
  const [viewportHeight, setViewportHeight] = useState(A4_VIEWPORT_HEIGHT_PX);

  const isTurning = incomingPage != null;
  const pagerPage = incomingPage ?? committedPage;

  const onDocumentLoadSuccess = useCallback(({ numPages: total }) => {
    setNumPages(total);
    setCommittedPage(1);
    setIncomingPage(null);
  }, []);

  const requestPage = useCallback(
    (target) => {
      if (!numPages || target < 1 || target > numPages) return;
      if (target === committedPage && !incomingPage) return;
      if (target === incomingPage) return;
      setIncomingPage(target);
    },
    [numPages, committedPage, incomingPage],
  );

  const handleIncomingRenderSuccess = useCallback(() => {
    setIncomingPage((target) => {
      if (target != null) {
        setCommittedPage(target);
      }
      return null;
    });
  }, []);

  const handlePageRenderSuccess = useCallback(({ height }) => {
    if (height > 0) {
      setViewportHeight(Math.ceil(height));
    }
  }, []);

  const goPrev = () => requestPage(pagerPage - 1);
  const goNext = () => requestPage(pagerPage + 1);

  const [printError, setPrintError] = useState(null);

  const handleDownload = () => {
    if (!previewPdfUrl) return;
    const a = document.createElement('a');
    a.href = previewPdfUrl;
    a.download = 'Crewchain-quick-start-guide.pdf';
    a.click();
  };

  const handlePrint = async () => {
    if (!previewPdfUrl) return;
    setPrintError(null);
    try {
      await printPdfFromUrl(previewPdfUrl);
    } catch (e) {
      setPrintError(e?.message || 'Could not open print dialog.');
    }
  };

  const showPager = numPages != null && numPages > 1;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 24,
        }}
      >
        <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--cc-text-primary)', margin: 0 }}>
          Quick start guide
        </h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button
            type="button"
            onClick={handlePrint}
            disabled={!previewPdfUrl}
            style={guideActionButtonStyle(!previewPdfUrl, 'secondary')}
          >
            <Printer size={18} />
            Print
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!previewPdfUrl}
            style={guideActionButtonStyle(!previewPdfUrl, 'primary')}
          >
            <Download size={18} />
            Download
          </button>
        </div>
      </div>

      {printError && (
        <p style={{ fontSize: 14, color: '#b91c1c', marginBottom: 16 }}>{printError}</p>
      )}

      {loading && (
        <p style={{ fontSize: 14, color: 'var(--cc-text-muted)', marginBottom: 16 }}>Loading guide…</p>
      )}

      {loadError && (
        <p style={{ fontSize: 14, color: '#b91c1c', marginBottom: 16 }}>
          {loadError} Ensure{' '}
          <code style={{ fontSize: 12 }}>{QUICK_START_GUIDE_STORAGE_PATH}</code> exists in Storage
          and rules allow read.
        </p>
      )}

      {previewPdfUrl && (
        <>
          <div
            style={{
              width: PREVIEW_WIDTH_PX,
              maxWidth: '100%',
              minHeight: viewportHeight,
              margin: '0 auto',
              boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
              position: 'relative',
              background: 'var(--cc-card-bg)',
            }}
          >
            <Document
              file={previewPdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <div
                  style={{
                    minHeight: viewportHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                    fontSize: 14,
                    color: 'var(--cc-text-muted)',
                  }}
                >
                  Loading pages…
                </div>
              }
            >
              <div style={{ position: 'relative', minHeight: viewportHeight }}>
                <Page
                  {...pageProps}
                  pageNumber={committedPage}
                  onRenderSuccess={handlePageRenderSuccess}
                />
                {incomingPage != null && incomingPage !== committedPage && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: PREVIEW_WIDTH_PX,
                    }}
                  >
                    <Page
                      {...pageProps}
                      pageNumber={incomingPage}
                      onRenderSuccess={handleIncomingRenderSuccess}
                    />
                  </div>
                )}
              </div>
            </Document>
            {isTurning && (
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(255, 255, 255, 0.35)',
                  pointerEvents: 'none',
                }}
              />
            )}
          </div>

          {showPager && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 16,
                marginTop: 20,
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                onClick={goPrev}
                disabled={pagerPage <= 1 || isTurning}
                aria-label="Previous page"
                style={pagerButtonStyle(pagerPage <= 1 || isTurning)}
              >
                <ChevronLeft size={20} />
                Previous
              </button>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--cc-text-secondary)',
                  minWidth: 100,
                  textAlign: 'center',
                }}
              >
                Page {pagerPage} of {numPages}
              </span>
              <button
                type="button"
                onClick={goNext}
                disabled={pagerPage >= numPages || isTurning}
                aria-label="Next page"
                style={pagerButtonStyle(pagerPage >= numPages || isTurning)}
              >
                Next
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function guideActionButtonStyle(disabled, variant) {
  const primary = variant === 'primary';
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 18px',
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 8,
    border: primary ? 'none' : '1px solid var(--branding-app-primary-ring, #bfdbfe)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    color: disabled
      ? '#94a3b8'
      : primary
        ? '#fff'
        : 'var(--branding-app-primary, #2563eb)',
    background: disabled
      ? '#f1f5f9'
      : primary
        ? 'var(--branding-app-primary, #2563eb)'
        : 'var(--branding-app-primary-soft, #eff6ff)',
  };
}

function pagerButtonStyle(disabled) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    fontSize: 14,
    fontWeight: 600,
    color: disabled ? '#94a3b8' : 'var(--branding-app-primary, #2563eb)',
    background: disabled ? '#f1f5f9' : 'var(--branding-app-primary-soft, #eff6ff)',
    border: `1px solid ${disabled ? '#e2e8f0' : 'var(--branding-app-primary-ring, #bfdbfe)'}`,
    borderRadius: 8,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
